import { Injectable } from '@angular/core';
import { FormationsGroups } from '@shared/models/formationsGroups.model';
import { Observable, of, tap } from 'rxjs';
import { StorageService } from 'src/app/core/services/storage.service';
import { AllFormation } from '../models/allFormation.model';
import { AllRole } from '../models/allRole.model';
import { Formation } from '../models/formation.model';
import { Paramedical } from '../models/paramedical.model';
import { Role } from '../models/role.model';
import { ParamedicalDal } from './../dal/paramedical.dal';

@Injectable({
  providedIn: 'root',
})
export class ParamedicalService {
  private formationsCache = new Map<string, any[]>();

  constructor(
    private paramedicalDal: ParamedicalDal,
    private storageService: StorageService
  ) {}

  getAllRoles(): Observable<AllRole[]> {
    return this.paramedicalDal.getAllRoles();
  }

  getAllFormations(): Observable<AllFormation[]> {
    return this.paramedicalDal.getAllFormations();
  }

  getAllFormationsGroups(): Observable<FormationsGroups[]> {
    return this.paramedicalDal.getAllFormationsGroups();
  }

  getRoles(): Observable<Role[]> {
    return this.paramedicalDal.getRoles();
  }

  getRolesById(rolesIds: string[]): Observable<Role[]> {
    return this.paramedicalDal.getRolesById(rolesIds);
  }

  addRole(name: string, priority: number): Observable<Role> {
    return this.paramedicalDal.addRole(name, priority);
  }

  modifyRole(id: string, priority: number): Observable<Role> {
    return this.paramedicalDal.modifyRole(id, priority);
  }

  deleteRole(id: string): Observable<any> {
    return this.paramedicalDal.deleteRole(id);
  }

  getFormations(): Observable<Formation[]> {
    const selectedHospitalsIds = this.storageService.getData('selectedHospitalsId')?.join(',');

    if (this.formationsCache.has(selectedHospitalsIds)) {
      return of(this.formationsCache.get(selectedHospitalsIds) as Formation[]);
    }

    return this.paramedicalDal.getFormations().pipe(
      tap((formations: Formation[]) => {
        this.formationsCache.set(selectedHospitalsIds, formations);
      })
    );
  }

  addFormation(name: string, groupedWith: string[]): Observable<Formation> {
    this.formationsCache = new Map<string, any[]>();

    return this.paramedicalDal.addFormation(name, groupedWith);
  }

  modifyFormation(id: string, groupedWith: string[]): Observable<Role> {
    this.formationsCache = new Map<string, any[]>();

    return this.paramedicalDal.modifyFormation(id, groupedWith);
  }

  deleteFormation(id: string): Observable<any> {
    this.formationsCache = new Map<string, any[]>();

    return this.paramedicalDal.deleteFormation(id);
  }

  getParamedicals(): Observable<Paramedical[]> {
    return this.paramedicalDal.getParamedicals();
  }

  addParamedical(formation: string, roles: string[], ratingSystem: boolean): Observable<Paramedical> {
    return this.paramedicalDal.addParamedical(formation, roles, ratingSystem);
  }

  modifyParamedical(id: string, formation: string, roles: string[], ratingSystem: boolean): Observable<Role> {
    return this.paramedicalDal.modifyParamedical(id, formation, roles, ratingSystem);
  }

  deleteParamedical(id: string): Observable<any> {
    return this.paramedicalDal.deleteParamedical(id);
  }

  getFormationsThatHaveSkills(): Observable<Formation[]> {
    return this.paramedicalDal.getFormationsThatHaveSkills();
  }

  clear() {
    this.formationsCache = new Map<string, any[]>();
  }
}
